import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Rocket, DollarSign, Users2, HeartHandshake, Trophy, Save } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function Profile() {
  const [p, setP] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/profile").then((r) => { setP(r.data); setForm(r.data); }); }, []);
  if (!p) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", {
        first_name: form.first_name, last_name: form.last_name, phone: form.phone,
        nmls_number: form.nmls_number,
        state_licenses: (form.state_licenses_str ?? (form.state_licenses || []).join(", ")).split(",").map((s) => s.trim()).filter(Boolean),
      });
      setP((prev) => ({ ...prev, ...data }));
      toast.success("Profile saved");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <div className="max-w-3xl">
      <PageHeader title="My Profile" subtitle="Your originator details and lifetime activity." />
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
        <StatCard label="Runs" value={p.runs_purchased ?? 0} icon={Rocket} accent="bg-slate-900 text-amber-400" />
        <StatCard label="Spent" value={money(p.total_spent)} icon={DollarSign} accent="bg-amber-100 text-amber-700" />
        <StatCard label="Prospects" value={p.total_prospects ?? 0} icon={Users2} accent="bg-blue-100 text-blue-700" />
        <StatCard label="Interested" value={p.total_interested ?? 0} icon={HeartHandshake} accent="bg-rose-100 text-rose-700" />
        <StatCard label="Conversions" value={p.total_conversions ?? 0} icon={Trophy} accent="bg-emerald-100 text-emerald-700" />
      </div>
      <Card className="p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><Label>First name</Label><Input className="mt-1.5" value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} data-testid="profile-first" /></div>
          <div><Label>Last name</Label><Input className="mt-1.5" value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} data-testid="profile-last" /></div>
          <div><Label>Email</Label><Input className="mt-1.5" value={form.email || ""} disabled /></div>
          <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone" /><p className="mt-1 text-xs text-slate-400">Required for AI calls — interested prospects are warm-transferred here.</p></div>
          <div><Label>Individual NMLS #</Label><Input className="mt-1.5" value={form.nmls_number || ""} onChange={(e) => set("nmls_number", e.target.value)} data-testid="profile-nmls" /></div>
          <div><Label>State licenses (comma-separated)</Label><Input className="mt-1.5" defaultValue={(form.state_licenses || []).join(", ")} onChange={(e) => set("state_licenses_str", e.target.value)} data-testid="profile-states" /></div>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>Status: <span className="font-semibold text-emerald-600 capitalize">{p.account_status}</span> · Joined {p.date_joined ? new Date(p.date_joined).toLocaleDateString() : "—"}</span>
          <Button onClick={save} disabled={saving} data-testid="profile-save-btn" className="bg-slate-900 hover:bg-slate-800"><Save className="mr-2 h-4 w-4" /> Save</Button>
        </div>
      </Card>
    </div>
  );
}
